/**
 * Map ArchiMate layers to a color palette inspired by the spec.
 * Used in the type-palette and canvas nodes.
 *
 * Sinds M5-§4 leest deze module uit `registry/notation-tokens.json` —
 * de single source of truth voor canvas-render-kleuren. Type-overrides
 * (Risk-pack rood, Security-pack groen) staan ook in dat bestand. Pas
 * dáár aan, niet hier.
 */
import {
  isCompatStereotype,
  type NotationToken,
  type NotationTokensFile,
  NotationTokensIndex,
  type StereotypeBundle,
} from "@ae/domain";
import notationTokens from "../../../../registry/notation-tokens.json";
import { getRegistry } from "../registry";
import type { DisplayMode } from "../state/displayMode";

/** De eigen notation van een profiel, of niets als het profiel onbekend is.
 *  Lui opgezocht en niet gecachet: de registry doet de lookup al met een map,
 *  en cachen zou een profielwijziging pas zichtbaar maken na een herlaad. */
function stereotypeNotation(id: string): StereotypeBundle["notation"] | undefined {
  try {
    return getRegistry().getStereotype(id)?.notation;
  } catch {
    // De registry is er niet bij een kale unit-test van deze module.
    return undefined;
  }
}

export interface LayerColor {
  bg: string;
  border: string;
  text: string;
}

const FALLBACK: LayerColor = { bg: "#f0f0f0", border: "#888888", text: "#333333" };

const tokensIndex = new NotationTokensIndex(notationTokens as unknown as NotationTokensFile);

function tokenToLayerColor(t: NotationToken | undefined): LayerColor | null {
  if (!t) return null;
  return {
    bg: t.fill ?? FALLBACK.bg,
    border: t.stroke ?? FALLBACK.border,
    text: t.text_color ?? FALLBACK.text,
  };
}

export function colorForLayer(layer: string): LayerColor {
  return tokenToLayerColor(tokensIndex.resolveLayer(layer)) ?? FALLBACK;
}

export function colorForType(typeId: string, layer: string): LayerColor {
  // resolveForType: type-token wint van layer-default; vervolgens
  // vallen we naar de built-in fallback.
  return tokenToLayerColor(tokensIndex.resolveForType(typeId, layer)) ?? FALLBACK;
}

/**
 * Polymorfe stereotypes — eenzelfde stereotype-id rendert anders
 * afhankelijk van het base-element-type (bv. `«Container»` op
 * ApplicationComponent = blauw / app-code; op SystemSoftware = groen
 * / infrastructure). De tokens.json-keys volgen de conventie
 * `<stereotype>.<suffix>`.
 */
const POLYMORPHIC_STEREOTYPE_SUFFIX: Record<string, Record<string, string>> = {
  "archimate.ApplicationComponent": {
    "c4.Container": "app-code",
  },
  "archimate.SystemSoftware": {
    "c4.Container": "infrastructure",
  },
  // BMC ValueProposition: motivation-bril (Value) vs business-bril
  // (BusinessService) — zie archimate-bmc-extensie.md §3.1.
  "archimate.Value": {
    "bmc.ValueProposition": "value",
  },
  "archimate.BusinessService": {
    "bmc.ValueProposition": "service",
  },
  // BMC CostStructure: outcome (financieel resultaat) vs driver
  // (financiële drijfveer) — zie §3.2.
  "archimate.Outcome": {
    "bmc.CostStructure": "outcome",
  },
  "archimate.Driver": {
    "bmc.CostStructure": "driver",
  },
};

/**
 * Legacy-fallback: vóór M5 gebruikten C4-diagrammen `c4_level` op
 * ApplicationComponent als discriminator (system / container /
 * component). Migreert in M8 naar stereotypes; tot die tijd
 * mappen we `c4_level` naar de bijbehorende stereotype-token zodat
 * pre-M5-data dezelfde kleuren krijgt als post-M5-data.
 */
const C4_LEVEL_FALLBACK_TOKEN: Record<string, string> = {
  system: "c4.SoftwareSystem",
  container: "c4.Container.app-code",
  component: "c4.Component",
};

/**
 * Token-resolutie voor één concept. Precedence:
 *
 *   1. Actieve stereotypes (in array-volgorde, last-applied = meest
 *      specifiek dus reverse-iteratie). Per stereotype eerst de
 *      polymorfe variant `<stereotype>.<suffix>` (afhankelijk van
 *      base-typeId), dan de plain `<stereotype>`.
 *   2. Legacy `c4_level`-discriminator op ApplicationComponent —
 *      alleen wanneer geen stereotype-hit en wanneer `c4_level`
 *      gezet is op een herkende waarde.
 *   3. Type-override (bv. een legacy type-token; sinds 2026-05-17 zijn
 *      Risk/Vulnerability/ControlObjective/SecurityPrinciple/Countermeasure
 *      verhuisd naar stereotypes en raken deze pad niet meer).
 *   4. Layer-default (`archimate.layer.<layer>`).
 *   5. Built-in fallback (#f0f0f0 / #888 / #333).
 */
export interface ConceptTokenInput {
  typeId: string;
  layer: string;
  attributes?: Record<string, unknown>;
  stereotypes?: readonly string[];
  /** ArchiMate-weergavemodus. Weglaten = niet filteren (het gedrag van
   *  vóór de v4-migratie). Zie `effectiveStereotypesForDisplay`. */
  displayMode?: DisplayMode;
}

/**
 * Filtert en herordent de stereotype-array op basis van de weergavemodus.
 *
 * - **v4**: compat-stereotypes vallen weg, dus het concept krijgt de kleur
 *   van z'n generieke Common-Domain-type (warmgrijs) — precies wat je wilt
 *   zien als je expliciet in de v4-bril kijkt.
 * - **v3.1**: compat-stereotypes komen er wél in, maar **vooraan**. Omdat
 *   `colorForConcept` de array reverse itereert (last-applied = meest
 *   specifiek) betekent vooraan de láágste precedentie. Zo krijgt een
 *   Assessment met zowel «Gap» als «Risk» nog steeds de rode Risk-kleur, en
 *   dient het compat-stereotype alleen als vangnet voor concepten die verder
 *   geen enkel inhoudelijk stereotype dragen.
 *
 * Pure functie — de aanroeper levert de modus, zodat een component 'm via
 * `useDisplayMode` kan lezen en dus her-rendert bij het omzetten.
 */
export function effectiveStereotypesForDisplay(
  stereotypes: readonly string[] | undefined,
  mode: DisplayMode | undefined,
): readonly string[] | undefined {
  if (!stereotypes || stereotypes.length === 0) return stereotypes;
  if (mode === undefined) return stereotypes;
  const own = stereotypes.filter((s) => !isCompatStereotype(s));
  if (mode === "v4") return own;
  const compat = stereotypes.filter(isCompatStereotype);
  if (compat.length === 0) return stereotypes;
  return [...compat, ...own];
}

export function colorForConcept(input: ConceptTokenInput): LayerColor {
  const { typeId, layer, attributes, displayMode } = input;
  const stereotypes = effectiveStereotypesForDisplay(input.stereotypes, displayMode);

  // 1. Stereotypes — reverse-iterate (most-specific = last-applied first)
  const stereotypeCandidates: string[] = [];
  const ordered: string[] = [];
  if (stereotypes && stereotypes.length > 0) {
    const polyMap = POLYMORPHIC_STEREOTYPE_SUFFIX[typeId];
    for (let i = stereotypes.length - 1; i >= 0; i--) {
      const s = stereotypes[i];
      if (!s) continue;
      const suffix = polyMap?.[s];
      if (suffix) stereotypeCandidates.push(`${s}.${suffix}`);
      stereotypeCandidates.push(s);
      ordered.push(s);
    }
  }
  const fromToken = tokensIndex.resolveFirst(stereotypeCandidates);
  if (fromToken) return tokenToLayerColor(fromToken) ?? FALLBACK;

  // 2. Geen token? Dan de eigen notation van het profiel. Dit staat bewust
  //    ná de tokens (een pack-profiel houdt z'n canonieke kleur) en vóór het
  //    basistype (een profiel is specifieker dan het type waarop het landt).
  //    Zonder deze stap is een zelf gedefinieerd profiel op het canvas niet
  //    van z'n basistype te onderscheiden.
  for (const s of ordered) {
    const own = stereotypeNotation(s);
    if (own?.fill || own?.stroke || own?.text_color) {
      return {
        bg: own.fill ?? FALLBACK.bg,
        border: own.stroke ?? own.fill ?? FALLBACK.border,
        text: own.text_color ?? FALLBACK.text,
      };
    }
  }

  const candidates: string[] = [];
  // 3. Legacy c4_level on ApplicationComponent (no stereotypes route applied)
  if (typeId === "archimate.ApplicationComponent") {
    const level = attributes?.c4_level;
    if (typeof level === "string") {
      const fallbackToken = C4_LEVEL_FALLBACK_TOKEN[level];
      if (fallbackToken) candidates.push(fallbackToken);
    }
  }

  // 4. Type-override + 5. Layer-default
  candidates.push(typeId, `archimate.layer.${layer}`);

  return tokenToLayerColor(tokensIndex.resolveFirst(candidates)) ?? FALLBACK;
}
